import {
	differenceInDays,
	differenceInMinutes,
	format,
	formatDistanceToNowStrict,
	isThisWeek,
	isToday,
	isYesterday,
} from "date-fns"

export const formatRelativeTime = (
	date: Date | string,
	addSuffix = false,
	isSnooze = false,
): string => {
	const parsedDate = typeof date === "string" ? new Date(date) : date

	if (isNaN(parsedDate.getTime())) {
		throw new Error("Invalid date provided")
	}

	const distance = formatDistanceToNowStrict(parsedDate, {
		addSuffix,
	})

	if (/seconds?/.test(distance)) {
		return isSnooze ? distance.replace(/ seconds?/, "sec") : "now"
	}

	return distance
		.replace(/ minutes?/, "min")
		.replace(/ hours?/, "h")
		.replace(/ days?/, "d")
		.replace(/ weeks?/, "w")
		.replace(/ months?/, "mo")
		.replace(/ years?/, "y")
}

// Absolute and readable ("Yesterday at 14:32", "July 7, 2026 at 14:32"), for
// places where "22 days ago" is not precise enough to act on.
export const formatReadableTimestamp = (date: Date | string): string => {
	const parsedDate = typeof date === "string" ? new Date(date) : date

	if (isNaN(parsedDate.getTime())) {
		throw new Error("Invalid date provided")
	}

	const now = new Date()
	const diffInMinutes = differenceInMinutes(now, parsedDate)
	const diffInDays = differenceInDays(now, parsedDate)

	if (diffInMinutes < 1) {
		return "now"
	}

	if (isToday(parsedDate)) {
		return `Today at ${format(parsedDate, "HH:mm")}`
	}

	if (isYesterday(parsedDate)) {
		return `Yesterday at ${format(parsedDate, "HH:mm")}`
	}

	if (diffInDays < 7 && isThisWeek(parsedDate)) {
		return format(parsedDate, "EEEE 'at' HH:mm")
	}

	const isCurrentYear = parsedDate.getFullYear() === now.getFullYear()

	if (isCurrentYear) {
		return format(parsedDate, "MMMM d 'at' HH:mm")
	}

	return format(parsedDate, "MMMM d, yyyy 'at' HH:mm")
}

// Relative while it still reads as recent, then a calendar date, because "5mo"
// tells you nothing you can act on.
export const formatCompactTimestamp = (date: Date | string): string => {
	const parsedDate = typeof date === "string" ? new Date(date) : date

	if (isNaN(parsedDate.getTime())) {
		throw new Error("Invalid date provided")
	}

	const now = new Date()

	if (differenceInDays(now, parsedDate) < 30) {
		return formatRelativeTime(parsedDate)
	}

	return parsedDate.getFullYear() === now.getFullYear()
		? format(parsedDate, "MMM d")
		: format(parsedDate, "MMM d, yyyy")
}
